/**
 * @module Service
 */

import { environment } from 'environments/environment';
import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import { Observable } from 'rxjs';
import { Profile, UserSignatory } from '@feature/profiles/shared';
import { Page, PageRequest } from '@app/models/page.model';
import { map } from 'rxjs/operators';

/**
 * Service for managing user profiles within the application.
 *
 * This service provides methods to create, update, retrieve, and delete user profile information.
 * It is provided at the root level, making it available throughout the application for any component
 * that requires access to user profile data.
 *
 * @service
 * @@Injectable
 */
@Injectable({ providedIn: 'root' })
export class ProfileService {
	/**
	 * Options for HTTP requests, including headers.
	 * @type {{ headers: HttpHeaders }}
	 * @note Used for the CRUD operations on the user.
	 */
	options: { headers: HttpHeaders } = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

	/**
	 * Creates an instance of the ProfileService.
	 *
	 * @constructor
	 * @param {HttpClient} _http - The HttpClient used for making HTTP requests.
	 */
	constructor(private _http: HttpClient) { }

	/**
	 * Retrieves a paginated list of profiles, optionally filtered by a search term.
	 *
	 * This method constructs the necessary HTTP parameters for pagination and search, then sends
	 * a GET request to retrieve the profiles from the API. The response is transformed to ensure
	 * that each profile is an instance of the Profile class.
	 *
	 * @param {string} [search] - Optional search term to filter the profiles.
	 * @param {PageRequest} [pageRequest] - Optional pagination parameters.
	 * @returns {Observable<Page<Profile>>} An observable containing the paginated list of profiles.
	 */
	public getProfiles(search?: string, pageRequest?: PageRequest): Observable<Page<Profile>> {
		const params = this.params(pageRequest, search);
		const result = this._http.get<Page<Profile>>(`${environment.config.apiUrl}/profiles`, { params });
		return result.pipe(map(page => {
			page.content = page.content.map(profile => Object.assign(new Profile(), profile));
			return page;
		}));
	}

	/**
	 * Retrieves the signatory associated with the specified profile ID.
	 *
	 * This method sends a GET request to fetch the signatory information for a given profile,
	 * identified by its unique ID.
	 *
	 * @param {number} profileId - The unique identifier of the profile for which to retrieve the signatory.
	 * @returns {Observable<UserSignatory>} An observable containing the signatory associated with the profile.
	 */
	public getProfileSignatory(profileId: number): Observable<UserSignatory> {
		return this._http.get<UserSignatory>(`${environment.config.apiUrl}/profiles/${profileId}/signatory`);
	}

	/**
	 * Retrieves the side-letter preview associated with the specified profile ID.
	 *
	 * This method sends a GET request to fetch the side-letter preview for a given profile,
	 * identified by its unique ID.
	 *
	 * @param {number} profileId - The unique identifier of the profile for which to retrieve the signatory.
	 * @returns {Observable<HttpResponse<Blob>>} An observable containing the side-letter preview associated with the profile.
	 */
	public getProfileSideLetterPreview(profileId: number): Observable<HttpResponse<Blob>> {
		const parameters: HttpParams = new HttpParams();
		const options = {
			params: parameters,
			observe: 'response' as const,  // 'const' assertion ensures the type is correctly interpreted.
			responseType: 'blob' as 'json' // 'json' assertion here is to satisfy TypeScript; it won't affect runtime.
		};
		return this._http.get<Blob>(`${environment.config.apiUrl}/profiles/${profileId}/preview-side-letter`, options);
	}

	/**
	 * Constructs HTTP parameters for pagination and search.
	 *
	 * This method creates an instance of HttpParams based on the provided pageable object
	 * and optional search term. It appends pagination and sorting parameters to the request.
	 *
	 * @param {PageRequest} [pageable] - Optional pagination parameters.
	 * @param {string} [search] - Optional search term to filter results.
	 * @returns {HttpParams} The constructed HTTP parameters.
	 */
	public params(pageable?: PageRequest, search?: string): HttpParams {
		if (! pageable) { pageable = { page: 0, size: 50, sortParams: [] }; }

		let params = new HttpParams();
		params = params.append('page', pageable.page.toString());
		params = params.append('size', pageable.size.toString());

		pageable.sortParams.forEach(sortParm => params = params.append('sort', sortParm));

		if (search) {
			params = params.append('search', search);
		}

		return params;
	}
}
